import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Paper,
    IconButton,
    Alert,
    CircularProgress
} from '@mui/material';
import { CloudUpload, Close, Warning } from '@mui/icons-material';
import i18n from '../../i18';

// Error Dialog Component
const ParseErrorDialog = ({ open, onClose, onRetry, errorCount }) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogContent sx={{ 'textAlign': 'center', 'py': 4 }}>
                <Box 
                    sx={{ 
                        'width': 80, 
                        'height': 50, 
                        'backgroundColor': '#f44336', 
                        'borderRadius': '50px',
                        'display': 'flex',
                        'alignItems': 'center',
                        'justifyContent': 'center',
                        'margin': '0 auto 24px auto'
                    }}
                >
                    <Warning sx={{ 'color': 'white', 'fontSize': 32 }} />
                </Box>
                
                <Typography variant="h5" gutterBottom sx={{ 'fontWeight': 'bold', 'mb': 3 }}>
                    Invalid file format
                </Typography>
                
                <Box sx={{ 
                    'backgroundColor': '#f5f5f5', 
                    'p': 2, 
                    'borderRadius': 2, 
                    'textAlign': 'left',
                    'mb': 3
                }}>
                    <Typography variant="body2" component="div">
                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                            <li>Check the file type — make sure it's a valid CSV file.</li>
                            <li>Review the file content — ensure all wallet numbers contain only letters, numbers, hyphens, and underscores.</li>
                            <li>Found {errorCount} invalid wallet number(s) with spaces or special characters.</li>
                            <li>Try uploading again — after fixing the file, re-upload it.</li>
                        </ul>
                    </Typography>
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ 'px': 3, 'pb': 3, 'gap': 1 }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined" 
                    fullWidth
                    sx={{ 'borderColor': '#ddd', 'color': '#666' }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={onRetry} 
                    variant="contained" 
                    fullWidth
                    sx={{ 'backgroundColor': '#f44336', '&:hover': { 'backgroundColor': '#d32f2f' } }}
                >
                    Try again
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const UploadCsvDialog = ({ open, onClose, onUpload }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorCount, setErrorCount] = useState(0);
    const fileInputRef = useRef(null);

    // Function to validate wallet number format
    const validateWalletNumber = (walletNumber) => {
        // Allow only alphanumeric characters, hyphens, and underscores
        const validPattern = /^[a-zA-Z0-9_-]+$/;
        return validPattern.test(walletNumber);
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check file type
            if (!file.name.toLowerCase().endsWith('.csv')) {
                setError(i18n.t('errors.invalidFileFormat'));
                return;
            }
            
            // Check file size (maximum 10MB)
            if (file.size > 10 * 1024 * 1024) {
                setError(i18n.t('errors.fileTooLarge'));
                return;
            }
            
            // Parse CSV file with walletNumber column
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
                
                console.log('=== CSV File Parsing ===');
                console.log(`Total lines found: ${lines.length}`);
                
                // Parse CSV content with validation
                const walletNumbers = [];
                const invalidEntries = [];
                
                lines.forEach((line, idx) => {
                    const trimmedLine = line.trim();
                    if (trimmedLine) {
                        // Handle CSV format - remove quotes if present and get first column
                        const walletNumber = trimmedLine.split(',')[0].replace(/"/g, '').trim();
                        
                        // Skip header if it contains 'walletNumber' text
                        if (idx === 0 && walletNumber.toLowerCase().includes('walletnumber')) {
                            console.log(`Header row: ${walletNumber}`);
                            return;
                        }
                        
                        // Validate wallet number format
                        if (validateWalletNumber(walletNumber)) {
                            walletNumbers.push(walletNumber);
                            console.log(`Row ${idx + 1}: ${walletNumber} ✓`);
                        } else {
                            invalidEntries.push({
                                row: idx + 1,
                                value: walletNumber,
                                reason: 'Contains spaces or invalid characters'
                            });
                            console.error(`Row ${idx + 1}: "${walletNumber}" ✗ - Contains spaces or invalid characters`);
                        }
                    }
                });
                
                console.log('=== Validation Results ===');
                console.log(`Valid wallet numbers: ${walletNumbers.length}`);
                console.log(`Invalid entries: ${invalidEntries.length}`);
                
                if (invalidEntries.length > 0) {
                    console.error('=== Invalid Entries Details ===');
                    invalidEntries.forEach(entry => {
                        console.error(`Row ${entry.row}: "${entry.value}" - ${entry.reason}`);
                    });
                    
                    // Show error dialog instead of setting error message
                    setErrorCount(invalidEntries.length);
                    setShowErrorDialog(true);
                    setSelectedFile(null); // Clear selected file
                } else {
                    // Clear any previous errors and proceed with valid wallet numbers
                    setError('');
                    console.log('=== Validation Successful ===');
                    console.log(`Proceeding with ${walletNumbers.length} valid wallet numbers`);
                    
                    // Store wallet numbers and proceed to next step
                    handleSuccessfulValidation(walletNumbers);
                }
                
                console.log('=== Parsed Valid Wallet Numbers ===');
                console.log('Valid wallet numbers array:', walletNumbers);
                console.log('=== End CSV Parsing ===');
            };
            reader.readAsText(file);
            
            setSelectedFile(file);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.name.toLowerCase().endsWith('.csv')) {
                // Parse CSV file with walletNumber column
                const reader = new FileReader();
                reader.onload = (e) => {
                    const text = e.target.result;
                    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
                    
                    console.log('=== CSV File Parsing (Drag & Drop) ===');
                    console.log(`Total lines found: ${lines.length}`);
                    
                    // Parse CSV content with validation
                    const walletNumbers = [];
                    const invalidEntries = [];
                    
                    lines.forEach((line, idx) => {
                        const trimmedLine = line.trim();
                        if (trimmedLine) {
                            // Handle CSV format - remove quotes if present and get first column
                            const walletNumber = trimmedLine.split(',')[0].replace(/"/g, '').trim();
                            
                            // Skip header if it contains 'walletNumber' text
                            if (idx === 0 && walletNumber.toLowerCase().includes('walletnumber')) {
                                console.log(`Header row: ${walletNumber}`);
                                return;
                            }
                            
                            // Validate wallet number format
                            if (validateWalletNumber(walletNumber)) {
                                walletNumbers.push(walletNumber);
                                console.log(`Row ${idx + 1}: ${walletNumber} ✓`);
                            } else {
                                invalidEntries.push({
                                    row: idx + 1,
                                    value: walletNumber,
                                    reason: 'Contains spaces or invalid characters'
                                });
                                console.error(`Row ${idx + 1}: "${walletNumber}" ✗ - Contains spaces or invalid characters`);
                            }
                        }
                    });
                    
                    console.log('=== Validation Results ===');
                    console.log(`Valid wallet numbers: ${walletNumbers.length}`);
                    console.log(`Invalid entries: ${invalidEntries.length}`);
                    
                    if (invalidEntries.length > 0) {
                        console.error('=== Invalid Entries Details ===');
                        invalidEntries.forEach(entry => {
                            console.error(`Row ${entry.row}: "${entry.value}" - ${entry.reason}`);
                        });
                        
                        // Show error dialog instead of setting error message
                        setErrorCount(invalidEntries.length);
                        setShowErrorDialog(true);
                        setSelectedFile(null); // Clear selected file
                    } else {
                        // Clear any previous errors and proceed with valid wallet numbers
                        setError('');
                        console.log('=== Validation Successful (Drag & Drop) ===');
                        console.log(`Proceeding with ${walletNumbers.length} valid wallet numbers`);
                        
                        // Store wallet numbers and proceed to next step
                        handleSuccessfulValidation(walletNumbers);
                    }
                    
                    console.log('=== Parsed Valid Wallet Numbers ===');
                    console.log('Valid wallet numbers array:', walletNumbers);
                    console.log('=== End CSV Parsing ===');
                };
                reader.readAsText(file);
                
                setSelectedFile(file);
            } else {
                setError(i18n.t('errors.invalidFileFormat'));
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError(i18n.t('errors.noFileSelected'));
            return;
        }

        setUploading(true);
        try {
            // TODO: Implement server upload
            /*
            await onUpload(selectedFile);
            */
            
            // Simulate successful upload for now
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            handleClose();
        } catch (err) {
            setError(err.message || i18n.t('errors.uploadFailed'));
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setError('');
        setUploading(false);
        setShowErrorDialog(false);
        setErrorCount(0);
        onClose();
    };

    const handleBrowseFiles = () => {
        fileInputRef.current?.click();
    };

    const handleErrorDialogClose = () => {
        setShowErrorDialog(false);
        setErrorCount(0);
    };

    const handleErrorDialogRetry = () => {
        setShowErrorDialog(false);
        setErrorCount(0);
        setSelectedFile(null);
        setError('');
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSuccessfulValidation = (walletNumbers) => {
        setUploading(true);
        try {
            onUpload(walletNumbers);
            handleClose();
        } catch (err) {
            setError(err.message || i18n.t('errors.uploadFailed'));
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ 'display': 'flex', 'justifyContent': 'space-between', 'alignItems': 'center' }}>
                    {i18n.t('dialogs.uploadCsv')}
                    <IconButton onClick={handleClose} disabled={uploading}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent>
                    <Box sx={{ 'mb': 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {i18n.t('dialogs.uploadCsvDescription')}
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ 'mb': 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Paper
                        sx={{
                            'p': 4,
                            'border': '2px dashed',
                            'borderColor': selectedFile ? 'success.main' : 'grey.300',
                            'textAlign': 'center',
                            'cursor': 'pointer',
                            'backgroundColor': selectedFile ? 'success.50' : 'grey.50',
                            '&:hover': {
                                'backgroundColor': selectedFile ? 'success.100' : 'grey.100'
                            }
                        }}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={handleBrowseFiles}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        
                        <CloudUpload 
                            sx={{ 
                                'fontSize': 48, 
                                'color': selectedFile ? 'success.main' : 'grey.400',
                                'mb': 2 
                            }} 
                        />
                        
                        {selectedFile ? (
                            <Box>
                                <Typography variant="h6" color="success.main" gutterBottom>
                                    {selectedFile.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {(selectedFile.size / 1024).toFixed(1)} KB
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {i18n.t('dialogs.dragDropFiles')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {i18n.t('dialogs.orClickToBrowse')}
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    <Box sx={{ 'mt': 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            {i18n.t('dialogs.csvRequirements')}
                        </Typography>
                    </Box>
                </DialogContent>
                
                <DialogActions>
                    <Button 
                        onClick={handleClose} 
                        color="inherit"
                        disabled={uploading}
                    >
                        {i18n.t('buttons.cancel')}
                    </Button>
                    <Button 
                        onClick={handleUpload} 
                        variant="contained"
                        disabled={!selectedFile || uploading}
                        startIcon={uploading ? <CircularProgress size={20} /> : null}
                    >
                        {uploading ? i18n.t('buttons.uploading') : i18n.t('buttons.upload')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ParseErrorDialog
                open={showErrorDialog}
                onClose={handleErrorDialogClose}
                onRetry={handleErrorDialogRetry}
                errorCount={errorCount}
            />
        </>
    );
};

export default UploadCsvDialog; 